"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useScoreQOpData = void 0;
const generateRowKeyFromRowIdxAndPageNo_1 = __importDefault(require("@opsbase/lib-data-query/helpers/generateRowKeyFromRowIdxAndPageNo"));
const calcUniqueKeyForViewField_1 = __importDefault(require("@opsbase/lib-data-viewfield/helpers/calcUniqueKeyForViewField"));
const ViewFieldKey_1 = __importDefault(require("@opsbase/lib-data-viewfield/ViewFieldKey"));
const formQuestionsToFormWithTree_1 = __importDefault(require("@opsbase/lib-form/convert/formQuestionsToFormWithTree"));
const FormQuestionType_1 = __importDefault(require("@opsbase/lib-form/FormQuestionType/FormQuestionType"));
const getAllQuestions_1 = __importDefault(require("@opsbase/lib-form/helpers/getAllQuestions"));
const getAnswerFieldTypeFromQuestionType_1 = __importDefault(require("@opsbase/lib-form/helpers/getAnswerFieldTypeFromQuestionType"));
const getScore_1 = __importDefault(require("@opsbase/lib-form/helpers/getScore"));
const getViewFieldsFromQuestions_1 = require("@opsbase/lib-form/helpers/getViewFieldsFromQuestions");
const lib_i18n_1 = __importDefault(require("@opsbase/lib-i18n"));
const lib_logger_1 = require("@opsbase/lib-logger");
const ChartingPalette_1 = __importDefault(require("@opsbase/lib-palette/ChartingPalette"));
const utils_1 = require("@opsbase/lib-time/utils");
const SelfContext_1 = __importDefault(require("@opsbase/react-ctrl-self/useSelf/SelfContext"));
const useDataTree_1 = __importDefault(require("@opsbase/web-ctrl-datatree/useDataTree"));
const useView_1 = __importDefault(require("@opsbase/web-ctrl-view/useView"));
const react_1 = require("react");
function useScoreQOpData(inverse = false, pageOffset = 0) {
    const dt = (0, useDataTree_1.default)();
    const view = (0, useView_1.default)(dt.viewId);
    const [changed, setChanged] = (0, react_1.useState)();
    const loading = (0, react_1.useRef)(true);
    const data = (0, react_1.useRef)([]);
    const lastDate = (0, react_1.useRef)();
    const lastInverse = (0, react_1.useRef)();
    const isLoading = dt.dataStatus.loading;
    const mostRecentChange = (0, utils_1.mostRecent)([
        dt.dataStatus.itemsLastLoaded,
        dt.dataStatus.groupsLastLoaded,
    ]);
    const items = dt.itemsForNode.get(dt.rootNode);
    const op = items === null || items === void 0 ? void 0 : items[0];
    const sectionQuestions = (0, react_1.useMemo)(() => { var _a, _b; return (_b = (_a = op === null || op === void 0 ? void 0 : op.form) === null || _a === void 0 ? void 0 : _a.filter(q => q.type === FormQuestionType_1.default.SECTION)) !== null && _b !== void 0 ? _b : []; }, [op === null || op === void 0 ? void 0 : op.form]);
    const { language } = (0, react_1.useContext)(SelfContext_1.default);
    // --------------------
    // This is the primary part of the hook
    // It parses the rawData into data that can be used by the charts
    (0, react_1.useEffect)(() => {
        // If the data isn't ready, we're not ready
        if (isLoading) {
            loading.current = true;
            lastDate.current = null;
            return;
        }
        // No need to update if data we're interested in hasn't changed
        const nothingChanged = lastDate.current &&
            lastDate.current >= mostRecentChange &&
            lastInverse.current === inverse;
        if (nothingChanged)
            return;
        // Now we prepare the data
        loading.current = false;
        lastInverse.current = inverse;
        lastDate.current = new Date();
        //const form = items[0]?.form
        const form = (0, formQuestionsToFormWithTree_1.default)(op === null || op === void 0 ? void 0 : op.form);
        const allQuestions = (0, getAllQuestions_1.default)(form.questionTree);
        let fields = sectionQuestions.map(s => {
            const questionNode = allQuestions.find(a => a.questionId === s.qId);
            return Object.assign(Object.assign({}, (0, getViewFieldsFromQuestions_1.calcLabelsFor)(questionNode, form, language)), { key: ViewFieldKey_1.default.QUESTIONANSWER, fieldType: (0, getAnswerFieldTypeFromQuestionType_1.default)(questionNode.type), questionNodeId: questionNode.nodeId, description: questionNode.title, width: 150, sortable: false, gqlFields: 'form, isScoreEnabled' });
        });
        if (!fields.length) {
            fields = view.fields.filter(f => f.key === ViewFieldKey_1.default.QUESTIONANSWER);
        }
        const palette = ChartingPalette_1.default.generatePalette(fields.length);
        data.current = fields
            .map((field, fieldIdx) => items
            .map((dataOp, itemIdx) => {
            const rowKey = (0, generateRowKeyFromRowIdxAndPageNo_1.default)(1 + itemIdx + pageOffset);
            const result = {
                x: itemIdx + 1,
                xDim: { id: rowKey, label: rowKey },
                y: 0,
                yRaw: 0,
                yDim: {
                    id: (0, calcUniqueKeyForViewField_1.default)(field),
                    label: field.label,
                },
                label: '',
                color1: palette[fieldIdx].main,
                color2: palette[fieldIdx].dark,
            };
            try {
                const { questionNodeId } = field;
                const dataForm = dataOp.getParsedForm();
                const formScore = (0, getScore_1.default)(dataForm);
                if (formScore.max <= 0)
                    return result;
                const question = dataForm.questions[questionNodeId];
                if (question) {
                    const score = (0, getScore_1.default)(dataForm, question);
                    const v = inverse
                        ? Math.max(score.max - score.total, 0)
                        : score.total;
                    const percOfForm = score.max / formScore.max;
                    const qPerc = v ? Math.floor((v / score.max) * 100) : 0;
                    const itemPerc = qPerc * percOfForm;
                    result.y = result.yRaw =
                        Math.round(((question.type === FormQuestionType_1.default.SECTION
                            ? qPerc
                            : itemPerc) +
                            Number.EPSILON) *
                            100) / 100;
                    result.label = inverse
                        ? lib_i18n_1.default.t('this-answer-missed', {
                            score: v,
                            max: score.max,
                            perc: qPerc,
                        })
                        : lib_i18n_1.default.t('this-answer-scored', {
                            score: v,
                            max: score.max,
                            perc: qPerc,
                        });
                }
            }
            catch (err) {
                lib_logger_1.Logger.error('Unexpected error in useScoreQOpData', err);
            }
            return result;
        })
            .filter(v => !!v))
            .filter(v => !!(v === null || v === void 0 ? void 0 : v.length));
        // Update Ctx
        setChanged(new Date());
    }, [
        items,
        inverse,
        isLoading,
        mostRecentChange,
        view,
        pageOffset,
        op,
        sectionQuestions,
        language,
    ]);
    // --------------------
    return (0, react_1.useMemo)(() => ({
        loading: loading.current,
        data: data.current,
        drawTable: !!sectionQuestions.length,
    }), 
    // eslint-disable-next-line
    [changed]);
}
exports.useScoreQOpData = useScoreQOpData;
exports.default = useScoreQOpData;
